import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Label } from './utils';
import { fetchBlogs } from '../components/data';

gsap.registerPlugin(ScrollTrigger);

// ── Preview card ──────────────────────────────────────────────────────────────
function PreviewCard({ post, isMobile }) {
  const underlineRef = useRef(null);

  const onEnter = (e) => {
    gsap.to(underlineRef.current, { scaleX: 1, duration: 0.4, ease: 'power3.out' });
    gsap.to(e.currentTarget, { y: -4, duration: 0.3, ease: 'power2.out' });
  };
  const onLeave = (e) => {
    gsap.to(underlineRef.current, { scaleX: 0, duration: 0.3, ease: 'power2.in' });
    gsap.to(e.currentTarget, { y: 0, duration: 0.4, ease: 'power2.out' });
  };

  return (
    <div
      className="blog-card"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{
        background: 'var(--black)',
        border: `1px solid ${post.color}22`,
        borderLeft: `2px solid ${post.color}`,
        borderRadius: 4,
        overflow: 'hidden',
        position: 'relative',
        // No opacity:0 here — GSAP sets it, so cards are visible if GSAP never runs
      }}
    >
      {/* Cover */}
      <div style={{ position: 'relative', height: isMobile ? 150 : 190, overflow: 'hidden' }}>
        <img
          src={post.cover}
          alt={post.title}
          loading="lazy"
          style={{
            width: '100%', height: '100%', objectFit: 'cover', display: 'block',
            filter: 'grayscale(30%) brightness(0.75)',
            transition: 'transform 0.6s ease, filter 0.4s ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'scale(1.04)';
            e.currentTarget.style.filter = 'grayscale(0%) brightness(0.9)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.filter = 'grayscale(30%) brightness(0.75)';
          }}
        />
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%',
          background: 'linear-gradient(to top, var(--black), transparent)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', top: '1rem', left: '1rem',
          fontFamily: 'Space Mono', fontSize: '0.55rem', letterSpacing: '0.15em',
          color: post.color, background: 'var(--black)',
          padding: '0.2rem 0.6rem', border: `1px solid ${post.color}66`,
        }}>
          {post.tag}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: isMobile ? '1.2rem' : '1.6rem 1.8rem' }}>
        <div style={{ height: 1, background: post.color, opacity: 0.3, marginBottom: '1.2rem' }} />
        <h3 style={{
          fontFamily: 'Syne, sans-serif', fontWeight: 800,
          fontSize: isMobile ? '1rem' : '1.05rem', letterSpacing: '-0.03em',
          lineHeight: 1.2, color: 'var(--white)', margin: '0 0 0.8rem',
        }}>
          {post.title}
        </h3>
        <p style={{
          fontFamily: 'Syne, sans-serif', fontSize: isMobile ? '0.8rem' : '0.85rem',
          color: 'var(--muted)', lineHeight: 1.7, margin: '0 0 1.2rem',
        }}>
          {post.excerpt}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: 'Space Mono', fontSize: '0.58rem', color: 'var(--muted)', letterSpacing: '0.1em' }}>
            {post.date}
          </span>
          <span style={{ fontFamily: 'Space Mono', fontSize: '0.58rem', color: post.color, letterSpacing: '0.1em' }}>
            {post.readTime} READ
          </span>
        </div>
        <div
          ref={underlineRef}
          style={{
            marginTop: '1rem', height: 1,
            background: post.color, transformOrigin: 'left', transform: 'scaleX(0)',
          }}
        />
      </div>
    </div>
  );
}

// ── Section component ─────────────────────────────────────────────────────────
export default function Blog({ loaded, isMobile }) {
  const sectionRef = useRef(null);
  const heroRef    = useRef(null);
  const gridRef    = useRef(null);
  const ctaRef     = useRef(null);

  const [allPosts, setAllPosts] = useState([]);
  const [fetching, setFetching] = useState(true);

  // Detect if this is the standalone /blog route (not embedded in portfolio scroll)
  const isStandalone = window.location.pathname === '/blog';

  useEffect(() => {
    fetchBlogs()
      .then(posts => setAllPosts(posts))
      .catch(err => console.error('Blog fetch error:', err))
      .finally(() => setFetching(false));
  }, []);

  const preview = allPosts.slice(0, 3);

  // ── Hero word wipe-in ─────────────────────────────────────────────────────
  useEffect(() => {
    // On standalone route, always animate regardless of `loaded` prop
    if (!isStandalone && !loaded) return;

    const ctx = gsap.context(() => {
      const words = heroRef.current?.querySelectorAll('.blog-word');
      if (!words?.length) return;

      if (isStandalone) {
        // Direct animate — no ScrollTrigger needed, already at top of page
        gsap.fromTo(words,
          { clipPath: 'inset(0 100% 0 0)', opacity: 0 },
          { clipPath: 'inset(0 0% 0 0)', opacity: 1, duration: 1.1, ease: 'power4.inOut', stagger: 0.12, delay: 0.2 }
        );
      } else {
        gsap.set(words, { clipPath: 'inset(0 100% 0 0)', opacity: 0 });
        ScrollTrigger.create({
          trigger: heroRef.current,
          start: 'top 85%',
          onEnter:     () => gsap.to(words, { clipPath: 'inset(0 0% 0 0)', opacity: 1, duration: 1.1, ease: 'power4.inOut', stagger: 0.12 }),
          onLeaveBack: () => gsap.to(words, { clipPath: 'inset(0 100% 0 0)', opacity: 0, duration: 0.4, stagger: 0.05 }),
        });
      }
    }, heroRef);
    return () => ctx.revert();
  }, [loaded, isStandalone]);

  // ── Card reveal ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (fetching || !preview.length) return;

    let ctx;
    const timer = setTimeout(() => {
      ctx = gsap.context(() => {
        const cards = gridRef.current?.querySelectorAll('.blog-card');
        if (!cards?.length) return;

        if (isStandalone) {
          // ── Standalone /blog page: skip ScrollTrigger entirely ──
          // Just animate directly — cards are already in the viewport
          cards.forEach((card, i) => {
            gsap.fromTo(card,
              { y: 48, opacity: 0, clipPath: 'inset(0 0 100% 0)' },
              { y: 0, opacity: 1, clipPath: 'inset(0 0 0% 0)', duration: 0.75, delay: 0.3 + i * 0.12, ease: 'power3.out' }
            );
          });
        } else {
          // ── Embedded in portfolio scroll: use ScrollTrigger ──
          cards.forEach((card, i) => {
            gsap.set(card, { y: 48, opacity: 0, clipPath: 'inset(0 0 100% 0)' });
            ScrollTrigger.create({
              trigger: card,
              start: 'top 95%',
              once: true,
              onEnter: () => gsap.to(card, {
                y: 0, opacity: 1, clipPath: 'inset(0 0 0% 0)',
                duration: 0.75, delay: i * 0.12, ease: 'power3.out',
              }),
            });
          });
          ScrollTrigger.refresh();
        }
      }, gridRef);
    }, 100);

    return () => {
      clearTimeout(timer);
      ctx?.revert();
    };
  }, [fetching, preview.length, isStandalone]);

  // ── CTA slide-in ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isStandalone && !loaded) return;

    const ctx = gsap.context(() => {
      const el = ctaRef.current;
      if (!el) return;

      if (isStandalone) {
        gsap.fromTo(el,
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.7, ease: 'power3.out', delay: 0.7 }
        );
      } else {
        gsap.set(el, { y: 30, opacity: 0 });
        ScrollTrigger.create({
          trigger: el,
          start: 'top 95%',
          once: true,
          onEnter: () => gsap.to(el, { y: 0, opacity: 1, duration: 0.7, ease: 'power3.out' }),
        });
      }
    });
    return () => ctx.revert();
  }, [loaded, isStandalone]);

  return (
    <section
      id="blog"
      ref={sectionRef}
      style={{
        background: 'var(--black)',
        padding: isMobile ? '5rem 5vw 6rem' : '8rem 5vw 10rem',
        position: 'relative',
        overflow: 'hidden', // NOT 'clip' — clip traps position:fixed cursor
      }}
    >
      {/* Grid texture */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
        backgroundImage: 'linear-gradient(var(--muted)08 1px, transparent 1px), linear-gradient(90deg, var(--muted)08 1px, transparent 1px)',
        backgroundSize: '60px 60px',
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>

        {/* Label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.2rem' }}>
          <Label n="07" text="Blog" />
        </div>

        {/* Hero title */}
        <div ref={heroRef} style={{ marginBottom: isMobile ? '3rem' : '5rem' }}>
          <h2 style={{
            fontFamily: 'Syne, sans-serif',
            fontSize: isMobile ? 'clamp(2rem, 10vw, 3rem)' : 'clamp(2.5rem, 5vw, 5rem)',
            fontWeight: 800, letterSpacing: '-0.05em', lineHeight: 0.9,
            margin: 0, display: 'flex', flexWrap: 'wrap', gap: '0 0.3em',
          }}>
            {['Writing', 'Things', 'Down'].map((word, i) => (
              <span
                key={i}
                className="blog-word"
                style={{ display: 'inline-block', color: i === 1 ? 'var(--acid)' : 'var(--white)' }}
              >
                {word}
              </span>
            ))}
          </h2>
          <p style={{
            fontFamily: 'Space Mono', fontSize: isMobile ? '0.65rem' : '0.72rem',
            color: 'var(--muted)', letterSpacing: '0.12em',
            marginTop: '1.5rem', maxWidth: 440,
          }}>
            FRONTEND EXPLORATIONS · ANIMATION PATTERNS · BUILD LOGS
          </p>
        </div>

        {/* Cards */}
        {fetching ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isMobile ? '1.5rem' : '2rem',
            marginBottom: isMobile ? '2.5rem' : '4rem',
          }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                height: isMobile ? 280 : 340,
                background: '#ffffff08',
                border: '1px solid #ffffff0f',
                borderRadius: 4,
              }} />
            ))}
          </div>
        ) : preview.length > 0 ? (
          <div
            ref={gridRef}
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: isMobile ? '1.5rem' : '2rem',
              marginBottom: isMobile ? '2.5rem' : '4rem',
            }}
          >
            {preview.map(post => (
              <PreviewCard key={post.id} post={post} isMobile={isMobile} />
            ))}
          </div>
        ) : (
          <div style={{
            marginBottom: isMobile ? '2.5rem' : '4rem',
            padding: '3rem',
            border: '1px solid #ffffff0f',
            borderRadius: 4,
            textAlign: 'center',
          }}>
            <p style={{ fontFamily: 'Space Mono', fontSize: '0.7rem', color: 'var(--muted)', letterSpacing: '0.12em' }}>
              NO POSTS YET
            </p>
          </div>
        )}

        {/* CTA */}
        <div ref={ctaRef} style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <button
            onClick={() => window.open('/blog', '_blank')}
            style={{
              fontFamily: 'Space Mono', fontSize: '0.7rem', letterSpacing: '0.14em',
              color: 'var(--black)', background: 'var(--acid)',
              border: '1px solid var(--acid)',
              padding: '0.85rem 2.2rem',
              cursor: 'pointer', borderRadius: 2,
              transition: 'background 0.25s, color 0.25s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--acid)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--acid)'; e.currentTarget.style.color = 'var(--black)'; }}
          >
            VIEW ALL POSTS ↗
          </button>
          {!fetching && (
            <span style={{ fontFamily: 'Space Mono', fontSize: '0.6rem', color: 'var(--muted)', letterSpacing: '0.1em' }}>
              {allPosts.length} POST{allPosts.length !== 1 ? 'S' : ''} TOTAL
            </span>
          )}
        </div>

        {/* Bottom rule */}
        <div style={{ marginTop: isMobile ? '3rem' : '5rem', height: 1, background: 'var(--muted)', opacity: 0.15 }} />
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          paddingTop: '1.5rem',
          fontFamily: 'Space Mono', fontSize: '0.6rem',
          color: 'var(--muted)', letterSpacing: '0.1em',
        }}>
          <span>BLOG / WRITING</span>
          <span style={{ color: 'var(--acid)' }}>NAVEEN ↗</span>
        </div>

      </div>
    </section>
  );
}